import {
  ErrorPage,
  getCookie,
  PingRedirect,
  setCookie,
  refreshAuthCookies,
  ScriptLoader,
  createBypassSession,
  isIdleExpired,
  logoutOnce,
  LOGIN_TYPE,
  getConfigData
} from "@cash/shared-components";
import { Snackbar, Alert } from "@mui/material";

import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import Homepage from "./Pages/Homepage";
import Login from "./Pages/Login";

function App() {
  const [activeUser, setActiveUser] = useState(null);
  const [loginType, setLoginType] = useState(() => getCookie("login_type"));
  const [expirationMinutes, setExpirationMinutes] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutInProgressRef = React.useRef(false);
  const bypassLogin = getCookie("login_type") === LOGIN_TYPE.BYPASS;

  const params = new URLSearchParams(window.location.search);
  const isAuthRequest = params.get("sling:authrequest") === "1";

  useEffect(() => {
    getConfigData().then((data) =>
      setExpirationMinutes(data.SESSION_EXPIRATION_MINUTES || 30)
    );
  }, []);

  useEffect(() => {
    if (!expirationMinutes) return;

    const bootstrapAuth = async () => {
      try {
        const res = await fetch("/api/user");
        if (!res.ok) throw new Error("no sso session");
        const user = await res.json();
        setActiveUser(user);
        setCookie("login_type", LOGIN_TYPE.SSO, expirationMinutes);
        setCookie("user_email", user.email, expirationMinutes);
        setCookie("pas", user.pas, expirationMinutes);
        setCookie("user_token", user.user_token, expirationMinutes);
        setLoginType(LOGIN_TYPE.SSO);
      } catch {
        if (bypassLogin) {
          const user = createBypassSession();
          setActiveUser(user);
          setLoginType(LOGIN_TYPE.BYPASS);
        }
      } finally {
        localStorage.setItem("lastActivity", Date.now().toString());
        setAuthReady(true);
      }
    };

    bootstrapAuth();
  }, [expirationMinutes, bypassLogin]);

  useEffect(() => {
    if (!expirationMinutes) return;

    const handleUserActivity = () => {
      if (logoutInProgressRef.current) return;
      if (isIdleExpired(expirationMinutes)) {
        logoutInProgressRef.current = true;
        setIsLoggingOut(true);
        setTimeout(() => {
          logoutOnce(loginType, expirationMinutes);
        }, 2000);
        return;
      }
      localStorage.setItem("lastActivity", Date.now().toString());
      refreshAuthCookies(expirationMinutes);
    };

    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
    };
  }, [expirationMinutes, loginType]);

  if (isLoggingOut) {
    return (
      <Snackbar
        open={true}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{
            width: "100%",
            whiteSpace: "pre-line",
            textAlign: "left",
            "& .MuiAlert-message": {
              textAlign: "left",
            },
          }}
        >
          Session expired due to inactivity. Redirecting to login.
        </Alert>
      </Snackbar>
    );
  }

  if (!authReady) return null;

  return (
    <Router basename="/">
      <ScriptLoader />
      <Routes>
        <Route
          path="/login"
          element={isAuthRequest || bypassLogin ? <Login /> : <PingRedirect />}
        />
        <Route
          path="/"
          element={activeUser || bypassLogin ? <Navigate to="/home" /> : <Navigate to="/login" />}
        />
        <Route
          path="/home"
          element={activeUser || bypassLogin ? <Homepage /> : <Navigate to="/login" />}
        />
        <Route path="/403" element={<ErrorPage expirationMinutes={expirationMinutes} />} />
      </Routes>
    </Router>
  );
}

export default App;
